import React from 'react'
import {
  MIN_DESCRIPTION_LENGTH,
  JOB_ATTACHMENT_MAX_SIZE_MB,
  JOB_ATTACHMENT_MAX_SIZE_BYTES,
  JOB_ATTACHMENT_OVERSIZE_MESSAGE,
  JOB_ATTACHMENT_UNSUPPORTED_MESSAGE,
} from 'business/direct_quote_request/constants'
import categoryAutocomplete from 'common/src/autocomplete/categoryAutocomplete'
import locationAutocomplete from 'common/src/autocomplete/locationAutocomplete'

const ACCEPTED_FILE_TYPES = 'image/png, image/jpeg, application/pdf'
const VALID_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf']

const fileTypeIsSupported = (file) => {
  const fileExtension = file.name.split('.').pop().toLowerCase()
  return VALID_EXTENSIONS.indexOf(fileExtension) > -1
}

const fileSizeIsSupported = (file) => file.size <= JOB_ATTACHMENT_MAX_SIZE_BYTES

const isImageFile = (file) => {
  if (file.type && file.type.indexOf('image/') === 0) return true

  return /\.(jpe?g|png)$/i.test(file.name)
}

const isPdfFile = (file) => {
  if (file.type === 'application/pdf') return true

  return /\.pdf$/i.test(file.name)
}

const dqrAutocompleteMenuOptions = {
  appendTo: document.body,
  position: {
    my: 'left top',
    at: 'left bottom',
    collision: 'flip fit',
  },
}

const attachDqrAutocompleteMenuToBody = (selector) => {
  $(selector).each(function configureMenu() {
    const $input = $(this)

    if (!$input.data('ui-autocomplete')) {
      return
    }

    $input.autocomplete('option', dqrAutocompleteMenuOptions)
  })
}

export default class JobDetails extends React.Component {
  constructor(props) {
    super(props)
    this.state = { fileErrors: '', attachmentPreviews: [] }
  }

  componentDidMount() {
    categoryAutocomplete.taskAutocomplete(
      '.js-dqr-subcategory-field',
      this.handleSelectedSubcategory,
      true
    )

    locationAutocomplete(
      '.js-dqr-suburb-field',
      this.handleSelectedSuburb
    )

    attachDqrAutocompleteMenuToBody('.js-dqr-subcategory-field, .js-dqr-suburb-field')

    this.initialiseAttachmentPreviews()
  }

  componentDidUpdate(prevProps, prevState) {
    const prevCount = (prevProps.attachments || []).length
    const nextCount = (this.props.attachments || []).length

    if (nextCount > prevCount) {
      this.scrollAttachmentSectionIntoView()
      return
    }

    if (!prevState.fileErrors && this.state.fileErrors) {
      this.scrollAttachmentSectionIntoView()
      return
    }

    const subcategoryErrorAppeared = !prevProps.subcategoryError && this.props.subcategoryError
    const suburbErrorAppeared = !prevProps.suburbError && this.props.suburbError

    if (subcategoryErrorAppeared || suburbErrorAppeared) {
      this.scrollServiceabilityErrorIntoView({
        subcategoryError: this.props.subcategoryError,
        suburbError: this.props.suburbError,
      })
    }
  }

  scrollServiceabilityErrorIntoView = ({ subcategoryError, suburbError }) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        let targetEl = null

        if (subcategoryError && this.subcategoryFieldEl) {
          targetEl = this.subcategoryFieldEl
        } else if (suburbError && this.suburbFieldEl) {
          targetEl = this.suburbFieldEl
        }

        if (!targetEl) return

        const scrollEl = this.props.stepScrollEl
        const canScrollContainer = scrollEl &&
          scrollEl.contains(targetEl) &&
          scrollEl.scrollHeight > scrollEl.clientHeight

        if (canScrollContainer) {
          const containerRect = scrollEl.getBoundingClientRect()
          const elementRect = targetEl.getBoundingClientRect()
          const offset = elementRect.top - containerRect.top + scrollEl.scrollTop

          scrollEl.scrollTo({ top: Math.max(0, offset - 8), behavior: 'smooth' })
          return
        }

        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })
  }

  setSubcategoryFieldRef = (el) => {
    this.subcategoryFieldEl = el
  }

  setSuburbFieldRef = (el) => {
    this.suburbFieldEl = el
  }

  scrollAttachmentSectionIntoView = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!this.attachmentSectionEl) return
        this.attachmentSectionEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    })
  }

  revokeAttachmentPreviews = () => {
    this.state.attachmentPreviews.forEach((previewUrl) => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    })
  }

  buildAttachmentPreviews = (attachments) => (
    (attachments || []).map((file) => (
      isImageFile(file) ? URL.createObjectURL(file) : null
    ))
  )

  componentWillUnmount() {
    this.revokeAttachmentPreviews()
    try { $('.js-dqr-subcategory-field').autocomplete('destroy') } catch (e) {}
    try { $('.js-dqr-suburb-field').autocomplete('destroy') } catch (e) {}
  }

  setAttachmentSectionRef = (el) => {
    this.attachmentSectionEl = el
  }

  initialiseAttachmentPreviews = () => {
    const attachments = this.props.attachments || []

    if (!attachments.length || this.state.attachmentPreviews.length === attachments.length) {
      return
    }

    this.setState({ attachmentPreviews: this.buildAttachmentPreviews(attachments) })
  }

  handleSelectedSubcategory = (event, response) => {
    const item = response && response.item
    if (!item) return

    this.props.onFieldChange('subcategoryId', item.id)
    this.props.onFieldChange('subcategoryName', item.value)
  }

  handleSelectedSuburb = (event, response) => {
    const item = response && response.item
    if (!item) return

    this.props.onFieldChange('suburbId', item.id)
    this.props.onFieldChange('suburbName', item.value)
  }

  handleSubcategoryTyping = (e) => {
    this.props.onFieldChange('subcategoryName', e.target.value)
    this.props.onFieldChange('subcategoryId', null)
  }

  handleSuburbTyping = (e) => {
    this.props.onFieldChange('suburbName', e.target.value)
    this.props.onFieldChange('suburbId', null)
  }

  handleUploadClick = () => {
    if (this.fileInputEl) {
      this.fileInputEl.click()
    }
  }

  processFiles = (event) => {
    const result = {
      fileNames: [],
      files: [],
      rejectedUnsupported: false,
      rejectedOversized: false,
    }
    const files = event.target.files
    const fileCount = files ? files.length : 0

    for (let i = 0; i < fileCount; i++) {
      const file = files[i]

      if (!fileTypeIsSupported(file)) {
        result.rejectedUnsupported = true
        continue
      }

      if (!fileSizeIsSupported(file)) {
        result.rejectedOversized = true
        continue
      }

      result.fileNames.push(file.name)
      result.files.push(file)
    }

    return result
  }

  handleUploadChange = (e) => {
    const result = this.processFiles(e)
    const existing = this.props.attachments || []

    this.setState({ fileErrors: '' })

    if (result.files.length) {
      const newPreviews = this.buildAttachmentPreviews(result.files)

      this.setState((prevState) => ({
        attachmentPreviews: prevState.attachmentPreviews.concat(newPreviews),
      }))
      this.props.onFieldChange('attachments', existing.concat(result.files))
    }

    if (result.rejectedUnsupported) {
      this.setState({ fileErrors: JOB_ATTACHMENT_UNSUPPORTED_MESSAGE })
    } else if (result.rejectedOversized) {
      this.setState({ fileErrors: JOB_ATTACHMENT_OVERSIZE_MESSAGE })
    }

    // Allow selecting the same file again after a prior pick.
    e.target.value = ''
  }

  handleRemoveAttachment = (index) => {
    const attachments = (this.props.attachments || []).slice()
    const previews = this.state.attachmentPreviews.slice()
    const previewUrl = previews[index]

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }

    attachments.splice(index, 1)
    previews.splice(index, 1)

    this.setState({ attachmentPreviews: previews })
    this.props.onFieldChange('attachments', attachments)
  }

  renderAttachmentThumbnail = (file, index) => {
    const previewUrl = this.state.attachmentPreviews[index]

    if (isImageFile(file) && previewUrl) {
      return (
        <img
          src={previewUrl}
          alt={file.name}
          className="w-full h-full object-cover rounded-5"
        />
      )
    }

    if (isPdfFile(file)) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-base-gray-10 rounded-5">
          <svg className="w-10 h-10 text-base-gray-text-secondary" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M5 17v-5h1.5a1.5 1.5 0 1 1 0 3H5m12 2v-5h2m-2 3h2M5 10V8c0-.4.1-.6.3-.8l4-4 .6-.2H18c.6 0 1 .4 1 1v6M5 19v1c0 .6.4 1 1 1h12c.6 0 1-.4 1-1v-1M10 3v4c0 .6-.4 1-1 1H5m6 4v5h1.4a1.6 1.6 0 0 0 1.6-1.6v-1.8a1.6 1.6 0 0 0-1.6-1.6H11Z" />
          </svg>
        </div>
      )
    }

    return (
      <div className="w-full h-full flex items-center justify-center bg-base-gray-10 rounded-5">
        <hero-icon-outline name="photograph" class="w-8 h-8 text-base-gray-text-secondary" />
      </div>
    )
  }

  renderAttachmentGrid = () => {
    const attachments = this.props.attachments || []

    return (
      <div className="pt-4 flex flex-wrap gap-2">
        {attachments.map((file, index) => (
          <div key={`${file.name}-${index}`} className="relative w-20 h-20 shrink-0">
            {this.renderAttachmentThumbnail(file, index)}
            <button
              type="button"
              className="absolute top-0 right-0 w-5 h-5 p-1 bg-surface rounded-full shadow flex items-center justify-center"
              onClick={() => this.handleRemoveAttachment(index)}
              aria-label={`Remove ${file.name}`}
            >
              <hero-icon-solid name="x" class="w-3 h-3 text-base-gray-text-secondary" />
            </button>
          </div>
        ))}
      </div>
    )
  }

  renderFieldError = (message) => {
    if (!message) return null

    return <p className="text-sm font-normal text-base-red-900">{message}</p>
  }

  fieldLabelClass = (hasError) => (
    `block text-base font-extrabold ${hasError ? 'text-base-red-900' : 'text-base-gray-text-main'}`
  )

  fieldInputClass = (hasError) => (
    `input-text-base ${hasError ? 'border-base-red-main border-2' : ''}`
  )

  render() {
    const {
      subcategoryName,
      suburbName,
      description,
      broadcastToOthers,
      businessName,
      attachments,
      subcategoryError,
      suburbError,
      onFieldChange,
    } = this.props

    const { fileErrors } = this.state
    const descriptionLength = (description || '').trim().length
    const descriptionRemaining = Math.max(0, MIN_DESCRIPTION_LENGTH - descriptionLength)
    const businessNameLabel = businessName || 'this business'
    const attachmentsCount = (attachments && attachments.length) || 0
    const hasAttachments = attachmentsCount > 0

    return (
      <div className="flex flex-col gap-5 lg:gap-6">
        <div className="p-4 bg-base-body rounded-lg">
          <div className="flex flex-col gap-2">
            <p className="text-base font-extrabold text-base-gray-text-main">Reach more business providers?</p>
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={broadcastToOthers}
                  onChange={(e) => onFieldChange('broadcastToOthers', e.target.checked)}
                />
                <div className="w-11 h-6 bg-base-gray-100 peer-focus:outline-hidden rounded-3xl peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-base-border after:border after:rounded-3xl after:h-5 after:w-5 after:transition-all peer-checked:bg-base-blue-700" />
              </label>
              <p className="flex-1 pt-0.5 text-sm font-medium text-base-gray-text-main leading-snug">
                Yes, allow other providers to receive my job and send quotes.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1" ref={this.setSubcategoryFieldRef}>
          <label className="block text-base font-extrabold text-base-gray-text-main">
            What service do you need?
          </label>
          <input
            type="text"
            className={`${this.fieldInputClass(Boolean(subcategoryError))} js-dqr-subcategory-field`}
            placeholder="e.g. Tiler, Cleaner, Electrician"
            value={subcategoryName}
            onChange={this.handleSubcategoryTyping}
          />
          {this.renderFieldError(subcategoryError)}
        </div>

        <div className="flex flex-col gap-1" ref={this.setSuburbFieldRef}>
          <label className="block text-base font-extrabold text-base-gray-text-main">
            Where are you located?
          </label>
          <input
            type="text"
            className={`${this.fieldInputClass(Boolean(suburbError))} js-dqr-suburb-field`}
            placeholder="Enter Postcode or Suburb"
            value={suburbName}
            onChange={this.handleSuburbTyping}
          />
          {this.renderFieldError(suburbError)}
        </div>

        <div className="flex flex-col gap-1">
          <label className="block text-base font-extrabold text-base-gray-text-main">
            What work do you need done?
          </label>
          <p className="text-sm font-medium text-base-gray-text-main">
            Write a message to <span className="font-semibold">{businessNameLabel}</span>. Describe the work required. Do not include phone numbers or exact address details.
          </p>
          <textarea
            className="text-area-base text-base h-[170px] lg:h-44 resize-none whitespace-pre-wrap"
            placeholder="Provide clear details about the task, size, and specific requirements."
            value={description}
            onChange={(e) => onFieldChange('description', e.target.value)}
          />
          <div className="flex items-center justify-between text-sm font-normal text-base-gray-text-secondary">
            <p>Minimum {MIN_DESCRIPTION_LENGTH} characters.</p>
            <p className="flex-1 text-right">({descriptionRemaining})</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="block text-base font-extrabold text-base-gray-text-main">
            Would you like to add photos or files?
          </label>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-base-gray-text-main">
              Feel free to upload some photos to help further explain your job.{' '}
              <span className="font-normal text-base-gray-text-secondary">(Optional)</span>
            </p>
            <input
              type="file"
              className="hidden"
              ref={(el) => { this.fileInputEl = el }}
              id="dqr-job-description-fileupload"
              accept={ACCEPTED_FILE_TYPES}
              multiple
              onChange={this.handleUploadChange}
            />
            <button
              type="button"
              className="flex w-full flex-col items-center justify-center gap-1 rounded-xs border border-dashed border-base-border bg-base-gray-10 px-0.5 py-6 md:min-h-[169px]"
              onClick={this.handleUploadClick}
            >
              <hero-icon-outline name="cloud-upload" class="size-8 text-base-gray-text-secondary" />
              <p className="text-sm text-base-gray-text-secondary">
                <span className="font-extrabold">Click to upload a file </span>
                <span className="font-normal">or drag and drop</span>
              </p>
              <p className="text-xxs font-normal text-base-gray-text-secondary">
                PDF, PNG, or JPG up to {JOB_ATTACHMENT_MAX_SIZE_MB}MB
              </p>
            </button>
          </div>
          <div ref={this.setAttachmentSectionRef}>
            {fileErrors && (
              <p className="text-sm text-base-red-900">{fileErrors}</p>
            )}
            {hasAttachments && this.renderAttachmentGrid()}
          </div>
        </div>
      </div>
    )
  }
}
